interface ExchangeRates {
  disclaimer: string;
  license: string;
  timestamp: number;
  base: string;
  rates: Record<string, number>;
}

export type SupportedCurrency = 'USD' | 'EUR' | 'GBP' | 'CNY';

export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  USD: '$',
  EUR: '€',
  CNY: '¥',
  GBP: '£',
};

class CurrencyService {
  private static instance: CurrencyService;
  private rates: Record<string, number> | null = null;
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 1000 * 60 * 60 * 12; // 12 hours cache (twice daily)
  private readonly API_URL = 'https://openexchangerates.org/api/latest.json';
  private readonly APP_ID = 'a9f321ff8f0a41ce90146c2edefe3cfd';

  private constructor() {}

  public static getInstance(): CurrencyService {
    if (!CurrencyService.instance) {
      CurrencyService.instance = new CurrencyService();
    }
    return CurrencyService.instance;
  }

  private async fetchRates(): Promise<void> {
    try {
      const now = Date.now();
      if (this.rates && now - this.lastFetch < this.CACHE_DURATION) {
        return; // Use cached rates
      }

      const response = await fetch(
        `${this.API_URL}?app_id=${this.APP_ID}&symbols=EUR,GBP,CNY`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch exchange rates');
      }

      const data: ExchangeRates = await response.json();
      this.rates = data.rates;
      this.lastFetch = now;

      console.log('Exchange rates updated:', {
        timestamp: new Date(data.timestamp * 1000).toISOString(),
        rates: this.rates
      });
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      throw error;
    }
  }

  /**
   * Convert amount from USD to target currency
   */
  public async fromUSD(amount: number, targetCurrency: SupportedCurrency): Promise<number> {
    if (targetCurrency === 'USD') return amount;
    
    await this.fetchRates();
    if (!this.rates || !this.rates[targetCurrency]) {
      throw new Error(`No exchange rate found for ${targetCurrency}`);
    }

    return amount * this.rates[targetCurrency];
  }

  /**
   * Convert amount from source currency to USD
   */
  public async toUSD(amount: number, sourceCurrency: SupportedCurrency): Promise<number> {
    if (sourceCurrency === 'USD') return amount;
    
    await this.fetchRates();
    if (!this.rates || !this.rates[sourceCurrency]) {
      throw new Error(`No exchange rate found for ${sourceCurrency}`);
    }

    return amount / this.rates[sourceCurrency];
  }

  /**
   * Get currency symbol
   */
  public getCurrencySymbol(currency: SupportedCurrency): string {
    return CURRENCY_SYMBOLS[currency];
  }
}

// Export singleton instance
export const currencyService = CurrencyService.getInstance();